import { fn } from '@ember/helper';
/* eslint-disable ember/no-tracked-properties-from-args */
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import MarketCustomReportingSidebar from 'lumen/components/market/custom-reporting/sidebar';
import type { SidebarCategory } from 'lumen/controllers/market/custom-reporting';
import type { LookerDashboard } from 'lumen/models';

interface MarketCustomReportingFormCopyDashboardSignature {
  Args: {
    afterCopy: (dashboard: LookerDashboard) => void;
    categories: SidebarCategory[];
    close: () => void;
    dashboard: LookerDashboard;
  };
}

export default class MarketCustomReportingFormCopyDashboard extends Component<MarketCustomReportingFormCopyDashboardSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];

  @tracked selectedFolder = this.args.dashboard.folder;

  copyTask = task({ drop: true }, async (): Promise<void> => {
    const { selectedFolder } = this;
    const { dashboard, afterCopy, close } = this.args;

    if (!selectedFolder) {
      return;
    }

    try {
      const copiedDashboard = (await dashboard.copy({
        folder_id: selectedFolder.id,
      })) as LookerDashboard;

      afterCopy(copiedDashboard);
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }

    close();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredCategories() {
    return this.args.categories.filter(({ type }) => type !== 'templates');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCopyDisabled() {
    const { selectedFolder } = this;

    return (
      !selectedFolder ||
      selectedFolder.isEmbedSharedRoot ||
      !selectedFolder.canEdit
    );
  }

  <template>
    <PrismModal
      @onClose={{@close}}
      @title={{t "custom_reporting.form.copy.title" title=@dashboard.title}}
      class="custom-reporting__modal"
      data-test-id="copy-modal"
    >
      <:default>
        <div>
          <MarketCustomReportingSidebar
            @categories={{this.filteredCategories}}
            @selectFolder={{fn (mut this.selectedFolder)}}
            @selectedDashboard={{@dashboard}}
            @selectedFolder={{this.selectedFolder}}
            @viewOnly={{true}}
          />
        </div>
      </:default>

      <:footer>
        <div class="flex h-full items-center justify-end gap-300 pr-500 pl-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@close}}
          />
          <PrismButton
            @isDisabled={{or this.isCopyDisabled this.copyTask.isRunning}}
            @isLoading={{this.copyTask.isRunning}}
            @label={{t "custom_reporting.copy_to_location"}}
            @onClick={{this.copyTask.perform}}
            data-test-id="copy-confirm-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
